import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Link as MuiLink,
  Divider,
  IconButton,
  Alert
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Modal de Vimeo
 * - isPublic: si es true, solo muestra las URLs (sin botón de eliminar)
 * - isManualMode: si es true y no es público, muestra el formulario para enviar a Vimeo
 * - video: objeto con información del video {id, nombre, vimeo: {videoId, url, embedUrl}}
 */
const VimeoModal = ({
  open,
  onClose,
  video,
  isPublic = false,
  isManualMode = false,
  onSendToVimeo,
  onDeleteFromVimeo,
  sending = false,
  deleting = false,
}) => {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [copySuccess, setCopySuccess] = React.useState('');

  // Resetear campos al abrir/cerrar
  React.useEffect(() => {
    if (open && video) {
      setTitle(video.nombre?.replace('.mp4', '') || '');
      setDescription('');
      setCopySuccess('');
    }
  }, [open, video]);

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${label} copiado`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  const handleSend = () => {
    if (onSendToVimeo && video) {
      // Si el título es el nombre del archivo sin .mp4, enviar vacío para que el backend genere el título con fecha
      const defaultTitle = video.nombre?.replace('.mp4', '');
      const titleToSend = (title === defaultTitle) ? '' : title;
      onSendToVimeo(video.id, titleToSend, description);
    }
  };

  const handleDelete = () => {
    if (onDeleteFromVimeo && video) {
      if (window.confirm('¿Estás seguro de eliminar este video de Vimeo? Esta acción no se puede deshacer.')) {
        onDeleteFromVimeo(video.id);
      }
    }
  };

  if (!video) return null;

  const hasVimeoInfo = video.vimeo && video.vimeo.videoId;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isPublic ? 'Ver en Vimeo' : (hasVimeoInfo ? 'Video en Vimeo' : 'Enviar a Vimeo')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Video:</strong> {video.nombre}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Si ya tiene info de Vimeo, mostrar URLs */}
        {hasVimeoInfo ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Enlaces de Vimeo:
            </Typography>

            {/* URL de visualización */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                URL del Video
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  value={video.vimeo.url}
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ flex: 1 }}
                />
                <IconButton 
                  size="small" 
                  onClick={() => handleCopy(video.vimeo.url, 'URL')}
                  title="Copiar"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  component={MuiLink}
                  href={video.vimeo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir en Vimeo"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* URL de embed */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                URL de Incrustación (Embed)
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  value={video.vimeo.embedUrl}
                  size="small"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ flex: 1 }}
                />
                <IconButton 
                  size="small" 
                  onClick={() => handleCopy(video.vimeo.embedUrl, 'Embed URL')}
                  title="Copiar"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {copySuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {copySuccess}
              </Alert>
            )}

            {/* Botón de eliminar (solo si no es público) */}
            {!isPublic && (
              <>
                <Divider sx={{ my: 2 }} />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={deleting}
                  fullWidth
                >
                  {deleting ? 'Eliminando...' : 'Eliminar de Vimeo'}
                </Button>
              </>
            )}
          </Box>
        ) : (
          /* Si no tiene info de Vimeo y no es público, mostrar formulario */
          !isPublic && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {isManualMode 
                  ? 'Este video no ha sido enviado a Vimeo. Completa la información para subirlo:' 
                  : 'La subida automática puede estar en proceso o haber fallado. También puedes subirlo manualmente:'}
              </Typography>

              <TextField
                label="Título"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                margin="normal"
                required
              />

              <TextField
                label="Descripción (opcional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSend}
                disabled={!title.trim() || sending}
                fullWidth
                sx={{ mt: 2 }}
              >
                {sending ? 'Enviando...' : 'Enviar a Vimeo'}
              </Button>
            </Box>
          )
        )}

        {!hasVimeoInfo && isPublic && (
          <Typography variant="body2" color="text.secondary" align="center">
            Este video no está disponible en Vimeo.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={sending || deleting}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VimeoModal;

